// ---------------------------------------------------
// Toggle
// module for handling toggle actions

import { TRIGGER_EVT } from "./events";
import toggle from "./toggleUtil";

export { TOGGLE_EVT, TRIGGER_EVT } from "./events";

interface ToggleConfig {
  activeClass: string;
  settingsAttr: string;
}

interface ToggleSetting {
  target: string;
  attribute: string;
  action?: string;
  value?: string | boolean;
  clickOutside?: boolean;
}

interface ToggleTarget extends Omit<ToggleSetting, "target"> {
  element: Element;
  value?: string | boolean;
  dispatchEvent?: boolean;
}

const defaultConfig: ToggleConfig = {
  activeClass: "is-active",
  settingsAttr: "data-toggle",
};

class Toggle {
  private element: HTMLElement;
  private config: ToggleConfig;
  private settings: ToggleSetting[];
  private targets: ToggleTarget[];
  private hasClickOutside: boolean;

  constructor(element: HTMLElement, config?: Partial<ToggleConfig>) {
    this.element = element;
    this.config = { ...defaultConfig, ...config };

    this.settings = [];
    this.targets = [];
    this.hasClickOutside = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    (this.element as any).ODS_Toggle = this;

    this.init();

    return this;
  }

  doToggle(props: ToggleTarget): ToggleTarget {
    const { dispatchEvent = true } = props;
    const { element, attribute, value: initialValue, action } = props;

    const returnProps = { ...props };
    const eventDetail = { trigger: this.element, element };

    let attributeToUse = attribute;
    let elementToUse = element;
    let value = initialValue;

    if (attributeToUse === "icon") {
      attributeToUse = "xlink:href";
      elementToUse = element.querySelector("use") as Element;
    }

    if (attributeToUse === "text") {
      elementToUse = (element.querySelector("[data-toggle-text-target]") ||
        element) as Element;
    }

    if (typeof value === "undefined") {
      value = false;
    }

    const toggleResult = toggle({
      element: elementToUse,
      attribute: attributeToUse,
      action,
      value,
      dispatchEvent,
      eventDetail,
    });

    if (toggleResult !== null) {
      returnProps.value = toggleResult as string | boolean;
    }

    return returnProps;
  }

  init(): ToggleTarget[] {
    this.settings = this.parseSettings();
    this.targets = this.setupTargets();

    // Zistí či nejaké nastavenie má clickOutside: true
    this.hasClickOutside = this.settings.some(
      (setting) => setting.clickOutside === true,
    );

    this.element.addEventListener("click", this.handleClick);

    // Pridá click outside listener iba ak je potrebný
    if (this.hasClickOutside) {
      document.addEventListener("click", this.handleClickOutside);
    }

    return this.targets;
  }

  handleClick(): void {
    this.targets = this.targets.map((target) => this.doToggle(target));
    this.element.dispatchEvent(new CustomEvent(TRIGGER_EVT, { bubbles: true }));
  }

  handleClickOutside(event: Event): void {
    const target = event.target as Element;

    // Ak klik nie je na trigger elemente ani na žiadnom z target elementov
    if (
      !this.element.contains(target) &&
      !this.targets.some((toggleTarget) =>
        toggleTarget.element.contains(target),
      )
    ) {
      // Zatvorí iba ak sú nejaké elementy otvorené (majú active class alebo nie sú hidden)
      const hasOpenTargets = this.targets.some((toggleTarget) => {
        if (
          toggleTarget.attribute === "class" &&
          typeof toggleTarget.value === "string"
        ) {
          return toggleTarget.element.classList.contains(toggleTarget.value);
        }
        if (toggleTarget.attribute === "hidden") {
          return !toggleTarget.element.hasAttribute("hidden");
        }
        return false;
      });

      if (hasOpenTargets) {
        this.targets = this.targets.map((toggleTarget) =>
          this.doToggle(toggleTarget),
        );
        this.element.dispatchEvent(
          new CustomEvent(TRIGGER_EVT, { bubbles: true }),
        );
      }
    }
  }

  destroy(): void {
    this.element.removeEventListener("click", this.handleClick);

    // Odstráni click outside listener ak bol pridaný
    if (this.hasClickOutside) {
      document.removeEventListener("click", this.handleClickOutside);
    }

    this.settings = [];
    this.targets = [];
    this.hasClickOutside = false;
  }

  update(): void {
    this.removeNonExistentTargets();
    this.addNewTargets();

    // Aktualizuje clickOutside nastavenie
    const wasClickOutside = this.hasClickOutside;
    this.hasClickOutside = this.settings.some(
      (setting) => setting.clickOutside === true,
    );

    // Pridá alebo odstráni click outside listener podľa potreby
    if (!wasClickOutside && this.hasClickOutside) {
      document.addEventListener("click", this.handleClickOutside);
    } else if (wasClickOutside && !this.hasClickOutside) {
      document.removeEventListener("click", this.handleClickOutside);
    }
  }

  static getTargetElementsFromString({
    target,
    trigger,
  }: {
    target: string;
    trigger: HTMLElement;
  }): Element[] {
    const selector = target.trim();

    if (selector === "self") {
      return [trigger];
    }

    if (selector === "parent") {
      return [trigger.parentNode as Element];
    }

    if (selector === "previous") {
      return [trigger.previousElementSibling as Element];
    }

    if (selector === "next") {
      return [trigger.nextElementSibling as Element];
    }

    if (typeof selector === "string") {
      // don't be tempted to remove this querySelectorAll
      // selector like '.target-elements' whould not work as expected
      return Array.from(document.querySelectorAll(selector));
    }

    // add log why we endup here
    return [];
  }

  parseSettings(): ToggleSetting[] {
    const jsonSettings = this.element.getAttribute(this.config.settingsAttr);
    const ariaControls = this.element.getAttribute("aria-controls");

    let settings: ToggleSetting[] = [];

    try {
      if (jsonSettings) {
        settings = JSON.parse(jsonSettings);
      }
    } catch (e) {
      console.warn("Invalid JSON in data-toggle attribute", e);
    }

    if (ariaControls) {
      const ariaExpanded = this.element.getAttribute("aria-expanded");

      settings = [
        ...(ariaExpanded
          ? [
              {
                target: "self",
                attribute: "aria-expanded",
              },
            ]
          : []),
        {
          target: ariaControls
            .split(" ")
            .map((a) => `#${a}`)
            .join(","),
          attribute: "hidden",
        },
        ...(Array.isArray(settings) ? settings : []),
      ];
    }

    return settings;
  }

  setupTargets(): ToggleTarget[] {
    const trigger = this.element;

    return this.settings.reduce<ToggleTarget[]>((allSettings, setting) => {
      if (!setting || !setting.target) {
        return allSettings;
      }

      return [
        ...allSettings,
        ...setting.target.split(",").reduce<ToggleTarget[]>(
          (targetSettings, target) => [
            ...targetSettings,
            ...Toggle.getTargetElementsFromString({
              target,
              trigger,
            }).map((element) => ({
              ...setting,
              element,
            })),
          ],
          [],
        ),
      ];
    }, []);
  }

  removeNonExistentTargets(): void {
    this.targets = this.targets.filter((target) =>
      document.contains(target.element),
    );
  }

  addNewTargets(): void {
    const allTargets = this.setupTargets();
    const newTargets = allTargets.filter(
      (newTarget) =>
        !this.targets.find(
          (target) =>
            target.element === newTarget.element &&
            target.action === newTarget.action,
        ),
    );

    this.targets = [...this.targets, ...newTargets];
  }

  static getInstance(el: HTMLElement): Toggle | null {
    return el && (el as any).ODS_Toggle ? (el as any).ODS_Toggle : null;
  }
}

export default Toggle;
